import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  ChevronDown,
  Flame,
  Keyboard,
  MessageCircle,
  Phone,
  PhoneCall,
  PhoneMissed,
  PhoneOff,
  Sparkles,
  SkipForward,
  Target,
  Trophy,
  X,
  Zap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";

/* -------------------------- Constants -------------------------- */
const STRATEGIES = [
  { v: "hot_first", l: "Hot first", hint: "Highest score → call first" },
  { v: "tasks_first", l: "Tasks first", hint: "Today's scheduled calls then hot" },
  { v: "newest", l: "Newest", hint: "Most recent registrations" },
  { v: "untouched", l: "Untouched", hint: "Oldest never-contacted" },
];

const DISPOSITIONS = [
  { k: "1", outcome: "connected", label: "Connected", Icon: PhoneCall, tone: "emerald" },
  { k: "2", outcome: "no_answer", label: "No answer", Icon: PhoneMissed, tone: "slate" },
  { k: "3", outcome: "busy", label: "Busy", Icon: PhoneOff, tone: "amber" },
  { k: "4", outcome: "wrong_number", label: "Wrong #", Icon: X, tone: "rose" },
  { k: "5", outcome: "not_interested", label: "Not interested", Icon: X, tone: "rose" },
  { k: "6", outcome: "callback_scheduled", label: "Schedule callback", Icon: Calendar, tone: "indigo" },
  { k: "7", outcome: "closed_won", label: "Closed · Won", Icon: Trophy, tone: "amber" },
  { k: "8", outcome: "whatsapp_sent", label: "WhatsApp sent", Icon: MessageCircle, tone: "emerald" },
];

const TONE_CLASSES = {
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100",
  slate: "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100",
  amber: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100",
  rose: "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100",
  indigo: "bg-indigo-50 text-indigo-800 border-indigo-200 hover:bg-indigo-100",
};

/* -------------------------- Helpers -------------------------- */
const tempPill = (temp) => {
  const map = {
    Hot: "bg-red-100 text-red-800 border-red-300",
    Warm: "bg-amber-100 text-amber-800 border-amber-300",
    Cold: "bg-slate-100 text-slate-700 border-slate-300",
  };
  return map[temp] || map.Cold;
};

function fmtPhone(p) {
  if (!p) return "—";
  // Strip ALL non-digits — including any leading '+' — so the `tel:` URI
  // we hand to the OS / dialer is pure digits. Some MyOperator / PBX setups
  // misinterpret '+' and append it to the dial string, producing junk like
  // '++919988…' or rejecting the call entirely.
  return String(p).replace(/\D/g, "");
}

function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/* -------------------------- Component -------------------------- */
export default function PowerDialerPage() {
  const { user } = useAuth();
  const [strategy, setStrategy] = useState("hot_first");
  const [strategyLoaded, setStrategyLoaded] = useState(false);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    calls_today: 0,
    connected: 0,
    closed_won: 0,
    callbacks_scheduled: 0,
    connect_rate: 0,
  });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackAt, setCallbackAt] = useState("");
  const [showShortcuts, setShowShortcuts] = useState(false);
  const notesRef = useRef(null);
  const initialLoadDone = useRef(false);

  const current = queue[index];

  /* ---- Load preferences once, then queue ---- */
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/me/preferences");
        setStrategy(r.data.preferences?.dialer_strategy || "hot_first");
      } catch {
        /* default */
      } finally {
        setStrategyLoaded(true);
      }
    })();
  }, []);

  const loadQueue = useCallback(
    async (s) => {
      setLoading(true);
      try {
        const [qr, sr] = await Promise.all([
          api.get(`/admin/dialer/queue?strategy=${s}&limit=50`),
          api.get("/admin/dialer/stats"),
        ]);
        setQueue(qr.data.leads || []);
        setIndex(0);
        setNotes("");
        setStats(sr.data || stats);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!strategyLoaded) return;
    loadQueue(strategy);
    initialLoadDone.current = true;
  }, [strategyLoaded, strategy, loadQueue]);

  const onStrategyChange = async (next) => {
    setStrategy(next);
    try {
      await api.patch("/admin/me/preferences", { strategy: next });
    } catch {
      /* non-blocking */
    }
  };

  /* ---- Disposition logging ---- */
  const logDisposition = useCallback(
    async (outcome, extra = {}) => {
      if (!current || submitting) return;
      setSubmitting(true);
      try {
        await api.post("/admin/dialer/disposition", {
          lead_id: current.id,
          outcome,
          notes: notes.trim() || null,
          ...extra,
        });
        const dispo = DISPOSITIONS.find((d) => d.outcome === outcome);
        toast.success(`Logged · ${dispo?.label || outcome}`, { duration: 1800 });
        // Refresh stats async
        api.get("/admin/dialer/stats").then((r) => setStats(r.data)).catch(() => {});
        // Auto-advance
        setNotes("");
        setIndex((i) => i + 1);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSubmitting(false);
      }
    },
    [current, submitting, notes]
  );

  const onCallbackConfirm = async () => {
    if (!callbackAt) {
      toast.error("Pick a callback time");
      return;
    }
    setCallbackOpen(false);
    await logDisposition("callback_scheduled", { follow_up_at: new Date(callbackAt).toISOString() });
    setCallbackAt("");
  };

  /* ---- Keyboard shortcuts ---- */
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in notes / inputs / dialog
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "textarea" || tag === "input" || tag === "select") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (callbackOpen) return;

      const k = e.key.toLowerCase();
      if (k === "c") {
        // Open phone dialer
        if (current?.phone) window.open(`tel:${fmtPhone(current.phone)}`);
        return;
      }
      if (k === "w") {
        if (current?.phone) {
          const num = fmtPhone(current.phone).replace(/^\+/, "");
          window.open(`https://wa.me/${num}`, "_blank");
        }
        return;
      }
      if (k === "n" || e.key === "ArrowRight") {
        setIndex((i) => (i + 1 < queue.length ? i + 1 : i));
        setNotes("");
        return;
      }
      if (k === "p" || e.key === "ArrowLeft") {
        setIndex((i) => (i > 0 ? i - 1 : 0));
        setNotes("");
        return;
      }
      if (k === "s") {
        setIndex((i) => (i + 1 < queue.length ? i + 1 : i));
        setNotes("");
        return;
      }
      if (k === "t") {
        // Focus notes field
        e.preventDefault();
        notesRef.current?.focus();
        return;
      }
      if (k === "?") {
        setShowShortcuts((v) => !v);
        return;
      }
      // Numeric disposition
      const found = DISPOSITIONS.find((d) => d.k === e.key);
      if (found) {
        if (found.outcome === "callback_scheduled") {
          // Pre-fill 1 day from now in local datetime input format
          const def = new Date();
          def.setDate(def.getDate() + 1);
          def.setHours(11, 0, 0, 0);
          const pad = (n) => String(n).padStart(2, "0");
          setCallbackAt(
            `${def.getFullYear()}-${pad(def.getMonth() + 1)}-${pad(def.getDate())}T${pad(def.getHours())}:${pad(def.getMinutes())}`
          );
          setCallbackOpen(true);
        } else {
          logDisposition(found.outcome);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, queue.length, logDisposition, callbackOpen]);

  /* ---- Render ---- */
  const queueDone = !loading && (queue.length === 0 || index >= queue.length);
  const counsellorName = user?.name || user?.email || "Counsellor";

  return (
    <div className="min-h-screen bg-[#F6F8FB]" data-testid="dialer-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-5 pl-16 py-3 flex items-center gap-4 md:pl-5">
          <div className="flex items-center gap-2 mr-auto">
            <div className="h-8 w-8 rounded-md bg-[#0A2540] flex items-center justify-center">
              <Zap className="h-4 w-4 text-[#C9A961]" />
            </div>
            <div>
              <div className="font-display text-base font-semibold text-[#0A2540] leading-tight">
                Power Dialer
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                {counsellorName} · Focus mode
              </div>
            </div>
          </div>

          {/* Strategy selector */}
          <Select value={strategy} onValueChange={onStrategyChange}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="dialer-strategy">
              <Target className="h-3.5 w-3.5 mr-1 text-[#C9A961]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STRATEGIES.map((s) => (
                <SelectItem key={s.v} value={s.v} className="text-xs">
                  <div className="flex flex-col">
                    <span className="font-medium">{s.l}</span>
                    <span className="text-[10px] text-slate-500">{s.hint}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            onClick={() => setShowShortcuts(true)}
            className="text-xs text-slate-500 hover:text-[#0A2540] inline-flex items-center gap-1 border border-slate-200 rounded-md px-2.5 py-1.5"
            data-testid="dialer-shortcuts-help"
          >
            <Keyboard className="h-3.5 w-3.5" />
            ?
          </button>
        </div>

        {/* Stats strip */}
        <div className="border-t border-slate-100 bg-slate-50/60">
          <div className="mx-auto max-w-6xl px-5 py-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
            <StatPill label="Calls today" value={stats.calls_today} testid="stat-calls" />
            <StatPill label="Connected" value={stats.connected} testid="stat-connected" />
            <StatPill label="Connect rate" value={`${stats.connect_rate}%`} testid="stat-connect-rate" />
            <StatPill label="Callbacks set" value={stats.callbacks_scheduled} testid="stat-callbacks" />
            <StatPill label="Won today" value={stats.closed_won} testid="stat-won" highlight />
            <span className="ml-auto text-slate-500">
              Lead {Math.min(index + 1, queue.length || 1)} of {queue.length} ·{" "}
              <span className="font-mono-clock">{strategy}</span>
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mx-auto max-w-3xl px-5 py-8">
        {loading ? (
          <div className="rounded-xl bg-white border border-slate-200 p-12 text-center text-slate-500">
            Building your queue…
          </div>
        ) : queueDone ? (
          <QueueClearedCard
            stats={stats}
            onReload={() => loadQueue(strategy)}
            data-testid="dialer-queue-cleared"
          />
        ) : (
          <LeadFocusCard
            lead={current}
            notesRef={notesRef}
            notes={notes}
            setNotes={setNotes}
            onLog={logDisposition}
            onSkip={() => {
              setIndex((i) => i + 1);
              setNotes("");
            }}
            onPrev={() => {
              setIndex((i) => Math.max(0, i - 1));
              setNotes("");
            }}
            onCallbackOpen={() => {
              const def = new Date();
              def.setDate(def.getDate() + 1);
              def.setHours(11, 0, 0, 0);
              const pad = (n) => String(n).padStart(2, "0");
              setCallbackAt(
                `${def.getFullYear()}-${pad(def.getMonth() + 1)}-${pad(def.getDate())}T${pad(def.getHours())}:${pad(def.getMinutes())}`
              );
              setCallbackOpen(true);
            }}
            disabled={submitting}
            index={index}
            total={queue.length}
          />
        )}
      </main>

      {/* Callback dialog */}
      <Dialog open={callbackOpen} onOpenChange={setCallbackOpen}>
        <DialogContent data-testid="dialer-callback-dialog">
          <DialogHeader>
            <DialogTitle>Schedule callback</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-xs text-slate-600 uppercase tracking-wider">
              When should we call back?
            </label>
            <Input
              type="datetime-local"
              value={callbackAt}
              onChange={(e) => setCallbackAt(e.target.value)}
              data-testid="callback-datetime"
            />
            <Textarea
              placeholder="Optional notes (e.g. 'Parent will be home after 6pm')"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallbackOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={onCallbackConfirm}
              className="bg-[#0A2540] text-white"
              data-testid="callback-confirm"
            >
              Schedule + Next lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Keyboard shortcuts dialog */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="h-5 w-5 text-[#C9A961]" />
              Keyboard shortcuts
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <KbdRow k="C" desc="Call current lead" />
            <KbdRow k="W" desc="WhatsApp current lead" />
            <KbdRow k="T" desc="Focus notes" />
            <KbdRow k="S / N / →" desc="Skip · next lead" />
            <KbdRow k="P / ←" desc="Previous lead" />
            <KbdRow k="?" desc="Toggle this help" />
            <div className="col-span-2 mt-2 mb-1 text-[10px] uppercase tracking-wider text-slate-500">
              Disposition
            </div>
            {DISPOSITIONS.map((d) => (
              <KbdRow key={d.outcome} k={d.k} desc={d.label} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------------- Sub-components ---------------------- */

function StatPill({ label, value, testid, highlight }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${
        highlight ? "text-[#C9A961]" : "text-slate-600"
      }`}
      data-testid={testid}
    >
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
      <span
        className={`font-mono-clock text-sm tabular-nums ${
          highlight ? "font-semibold" : "text-[#0A2540]"
        }`}
      >
        {value}
      </span>
    </span>
  );
}

function LeadFocusCard({
  lead,
  notes,
  setNotes,
  notesRef,
  onLog,
  onSkip,
  onPrev,
  onCallbackOpen,
  disabled,
  index,
  total,
}) {
  if (!lead) return null;
  const phone = fmtPhone(lead.phone);
  const tel = `tel:${phone}`;
  const wa = `https://wa.me/${phone.replace(/^\+/, "")}`;
  const lastDispo = lead.last_disposition;
  const lastNote = (lead.notes || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean)[0];

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 shadow-[0_30px_60px_-30px_rgba(10,37,64,0.18)] overflow-hidden"
      data-testid="dialer-lead-card"
    >
      {/* Top */}
      <div className="px-7 py-6 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge
                variant="outline"
                className={`${tempPill(lead.lead_temperature)} text-[10px] uppercase tracking-wider`}
                data-testid="lead-temp-badge"
              >
                <Flame className="h-3 w-3 mr-0.5" />
                {lead.lead_temperature || "Cold"} · {Math.round(lead.lead_score || 0)}
              </Badge>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                {lead.status || "New"}
              </Badge>
              {lastDispo && (
                <span className="text-[10px] text-slate-500">
                  Last: {lastDispo.outcome_label} · {relativeTime(lastDispo.created_at)}
                </span>
              )}
            </div>
            <h2 className="font-display text-3xl font-bold text-[#0A2540] leading-tight truncate">
              {lead.name}
            </h2>
            <div className="mt-1 text-sm text-slate-600 truncate">
              {lead.email} · {lead.preferred_country || "—"}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">NEET</div>
            <div className="font-mono-clock text-2xl text-[#0A2540]">
              {lead.neet_score || "—"}
            </div>
          </div>
        </div>

        {/* Quick facts row */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <FactCell label="Phone" value={phone} mono />
          <FactCell label="Parent" value={lead.parent_involvement || "—"} />
          <FactCell label="Budget" value={lead.budget_band || "—"} />
          <FactCell label="Source" value={lead.source || "Direct"} />
        </div>

        {/* Existing notes */}
        {lead.notes && (
          <details className="mt-5 group" data-testid="lead-history">
            <summary className="text-xs uppercase tracking-wider text-slate-500 cursor-pointer flex items-center gap-1.5 hover:text-[#0A2540]">
              <ChevronDown className="h-3.5 w-3.5 group-open:rotate-180 transition-transform" />
              History &amp; notes
            </summary>
            <pre className="mt-3 whitespace-pre-wrap text-xs text-slate-700 bg-slate-50 rounded-md p-3 border border-slate-100 max-h-40 overflow-auto">
              {lead.notes}
            </pre>
          </details>
        )}
      </div>

      {/* Call CTAs */}
      <div className="px-7 py-6 bg-gradient-to-br from-[#FAF9F6] to-white">
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={tel}
            className="flex-1 inline-flex items-center justify-center gap-2 h-14 rounded-xl bg-[#0A2540] hover:bg-[#1E3A5F] text-white font-semibold text-base transition-colors"
            data-testid="dialer-call-button"
          >
            <Phone className="h-5 w-5" />
            Call {phone}
            <kbd className="ml-2 text-[10px] bg-white/15 px-1.5 py-0.5 rounded">C</kbd>
          </a>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 h-14 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
            data-testid="dialer-wa-button"
          >
            <MessageCircle className="h-5 w-5" />
            WhatsApp
            <kbd className="ml-1 text-[10px] bg-white/15 px-1.5 py-0.5 rounded">W</kbd>
          </a>
        </div>
        <div className="mt-3">
          <VoiceNoteRecorder
            leadId={lead.id}
            onTranscribed={(text) => {
              if (!text) return;
              // Transcript already saved to lead.notes server-side.
              // Focus the dialer notes textarea so counsellor can add any
              // disposition-specific note before logging the outcome.
              notesRef?.current?.focus();
            }}
          />
        </div>
      </div>

      {/* Disposition grid */}
      <div className="px-7 py-6 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3 flex items-center gap-2">
          <Sparkles className="h-3 w-3 text-[#C9A961]" />
          Log the call outcome — press a number or click below
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {DISPOSITIONS.map((d) => {
            const isCallback = d.outcome === "callback_scheduled";
            return (
              <button
                key={d.outcome}
                onClick={() => (isCallback ? onCallbackOpen() : onLog(d.outcome))}
                disabled={disabled}
                data-testid={`dispo-${d.outcome}`}
                className={`relative px-3 py-3 rounded-lg border text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${TONE_CLASSES[d.tone]}`}
              >
                <kbd className="absolute top-1.5 right-1.5 text-[10px] bg-white/70 border border-slate-200 px-1 rounded">
                  {d.k}
                </kbd>
                <d.Icon className="h-4 w-4 mb-1.5" />
                <div className="text-left leading-tight">{d.label}</div>
              </button>
            );
          })}
        </div>

        {lastNote && (
          <div
            className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2"
            data-testid="dialer-last-note"
          >
            <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-amber-700/80">
              Last note
            </div>
            <p className="mt-0.5 line-clamp-2 text-xs text-slate-700">{lastNote}</p>
          </div>
        )}

        <Textarea
          ref={notesRef}
          rows={2}
          placeholder="Quick note (optional) — press T to focus"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-3 resize-none"
          data-testid="dialer-notes"
        />

        <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPrev}
            disabled={index === 0}
            data-testid="dialer-prev"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Prev
          </Button>
          <div className="font-mono-clock">
            {index + 1} / {total}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onSkip}
            data-testid="dialer-skip"
            className="text-slate-600"
          >
            Skip <SkipForward className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function FactCell({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 text-sm text-[#0A2540] truncate ${mono ? "font-mono-clock" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function QueueClearedCard({ stats, onReload }) {
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-12 text-center"
      data-testid="dialer-queue-cleared"
    >
      <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
        <Check className="h-7 w-7 text-emerald-700" />
      </div>
      <h2 className="mt-5 font-display text-3xl font-bold text-[#0A2540]">
        Queue cleared.
      </h2>
      <p className="mt-2 text-slate-600">
        You made <strong>{stats.calls_today}</strong> calls today ·{" "}
        <strong>{stats.connected}</strong> connected ·{" "}
        <strong>{stats.connect_rate}%</strong> connect rate.
      </p>
      <Button onClick={onReload} className="mt-6 bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="dialer-reload">
        <ArrowRight className="h-4 w-4 mr-1" />
        Reload queue
      </Button>
    </div>
  );
}

function KbdRow({ k, desc }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <kbd className="font-mono-clock bg-slate-100 border border-slate-200 px-2 py-0.5 rounded text-[11px]">
        {k}
      </kbd>
      <span className="text-slate-700">{desc}</span>
    </div>
  );
}
